import { type ApiError, errorMessage, isApiError } from '../../api/client'

/**
 * Client-side validation with the server's own zod schemas (P-UI-008, GAP-UI-016): the
 * limits can't drift. The server still validates everything.
 */
export type FieldErrors = Record<string, string>

/** The slice of a zod schema this needs (keeps `zod` out of the web package's imports). */
export interface Schema<T> {
  safeParse(
    value: unknown,
  ):
    | { success: true; data: T }
    | { success: false; error: { issues: { path: PropertyKey[]; message: string }[] } }
}

export function validate<T>(
  schema: Schema<T>,
  value: unknown,
): { ok: true; data: T } | { ok: false; errors: FieldErrors } {
  const parsed = schema.safeParse(value)
  if (parsed.success) return { ok: true, data: parsed.data }
  const errors: FieldErrors = {}
  for (const issue of parsed.error.issues) {
    const key = issue.path.map(String).join('.') || '_form'
    if (!(key in errors)) errors[key] = issue.message
  }
  return { ok: false, errors }
}

/** Field errors from a 422, plus a form-level message for everything else. */
export function serverErrors(error: unknown): { fields: FieldErrors; message: string | null } {
  if (isApiError(error)) {
    const apiError = error as ApiError
    const fields = apiError.fieldErrors
    if (Object.keys(fields).length > 0) return { fields, message: null }
    return { fields: {}, message: apiError.message }
  }
  return { fields: {}, message: error ? errorMessage(error) : null }
}
